import { ClassValue } from 'clsx';
import { GeolocationMapPreview } from '@shared/components/Geolocation';
import { Icon } from '@shared/components/Icon';
import { UserImage } from '@shared/components/Image';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { LoadingIndicator } from '@shared/components/loading';
import { GeolocationField } from '@shared/data/entity/fields/GeolocationField';
import { InputUserAndDate } from '@shared/scenes/entity/components/InputUserAndDate';
import { grid } from '@shared/styles';
import { formatDateTime } from '@shared/util/date';
import { CollectionViewFieldContainer } from './CollectionViewFieldContainer';
import { FieldVariant } from './FieldVariant';
import { useFieldUser } from './PersonRefsContext';
import { getShortName } from './util/getShortName';
import styles from './GeolocationCollectionViewField.module.css';

interface Props {
  variant: FieldVariant;
  className?: ClassValue;
  field: GeolocationField;
}

/**
 * Default: the map, and beside it who captured it, avatar and name, over when. Compact: a pin, the capturer's avatar and
 * the date. Inline: a pin and the capturer's short name, "Alex R.".
 */
export function GeolocationCollectionViewField({ field, variant, className }: Props) {
  if (field.isEmpty) {
    return null;
  }

  const isInline = variant === 'inline';
  const isCompact = variant === 'compact';
  const isDefault = variant === 'default';

  return (
    <CollectionViewFieldContainer
      fieldType={field.fieldType}
      variant={variant}
      className={[styles.geolocationCollectionViewField, className]}
    >
      {isInline && <InlineGeolocation field={field} />}
      {isCompact && <CompactGeolocation field={field} />}
      {isDefault && <DefaultGeolocation field={field} />}
    </CollectionViewFieldContainer>
  );
}

function DefaultGeolocation({ field }: { field: GeolocationField }) {
  const { coords, date, userId } = field.geolocation;
  const { user, loading } = useFieldUser(userId);
  return (
    <View row centerVertically className={styles.snapshot}>
      <GeolocationMapPreview width={grid(5)} height={grid(5)} lat={coords.lat} lon={coords.lon} />
      {loading && <LoadingIndicator />}
      {!loading && <InputUserAndDate user={user} date={date} collapsed />}
    </View>
  );
}

function CompactGeolocation({ field }: { field: GeolocationField }) {
  const { user } = useFieldUser(field.geolocation.userId);
  return (
    <>
      <View row centerVertically className={styles.marks}>
        <Icon name="pin-location" gridSize={3.5} className={styles.icon} />
        <UserImage user={user} size="small" className={styles.avatar} />
      </View>
      <Text weight="normal" numberOfLines={1}>
        {formatDateTime(field.geolocation.date)}
      </Text>
    </>
  );
}

function InlineGeolocation({ field }: { field: GeolocationField }) {
  const { user } = useFieldUser(field.geolocation.userId);
  return (
    <>
      <Icon name="pin-location" gridSize={2} className={styles.inlineIcon} />
      {getShortName(user)}
    </>
  );
}
